import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, Download, MessageCircle, Sparkles, RefreshCw, MapPin, Wallet, Hotel, Compass,
} from "lucide-react";
import Seo from "../components/Seo";
import InquiryForm from "../components/InquiryForm";
import DestinationAutocomplete from "../components/DestinationAutocomplete";
import { useSite } from "../components/SiteContext";
import { PageHero, Section, SectionHead, RichText } from "../components/shared";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { generatePlan, planPdfUrl, inr, waLink } from "../lib/api";
import { trackItineraryGenerated, trackItineraryDownload } from "../lib/analytics";
import { fadeUp, stagger } from "../lib/motion";

const PLANNER_CRUMBS = [{ label: "Home", to: "/" }, { label: "Plan My Journey", to: "/plan-my-journey" }];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];
const TYPES = ["couple", "honeymoon couple", "family with children", "family with teens",
  "solo traveller", "group of friends", "multi-generational family", "corporate group"];
const INTERESTS = ["Fine dining", "Culture & heritage", "Wildlife & safari", "Beaches",
  "Adventure", "Wellness & spa", "Photography", "Shopping", "Nightlife", "Nature & hiking",
  "Wine & vineyards", "Road trips", "Festivals", "Architecture"];

export default function Planner() {
  const { settings } = useSite();
  const [f, setF] = useState({
    destination: "", origin: "Delhi, India", month: "", duration_days: 7, travellers: 2,
    traveller_type: "couple", budget_inr: 400000, interests: [],
  });
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleInterest = (i) =>
    setF((p) => ({
      ...p,
      interests: p.interests.includes(i)
        ? p.interests.filter((x) => x !== i)
        : [...p.interests, i],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.destination.trim()) return toast.error("Tell us a destination or a region to explore.");
    if (!f.month) return toast.error("Please pick a travel month.");
    setBusy(true);
    setPlan(null);
    try {
      const res = await generatePlan({ ...f, budget_inr: Number(f.budget_inr) });
      setPlan(res.itinerary);
      setPlanId(res.id);
      trackItineraryGenerated({
        destination: res.itinerary.primary_destination,
        days: res.itinerary.days?.length,
        travellers: Number(f.travellers) || undefined,
        budgetInr: Number(f.budget_inr) || undefined,
        tier: res.itinerary.hotel_tier,
      });
      toast.success("Your draft itinerary is ready", {
        description: "A travel designer can refine it with you.",
      });
      setTimeout(
        () => document.getElementById("result")?.scrollIntoView({ behavior: "smooth" }),
        200
      );
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Please slow down a moment", {
          description:
            err?.response?.data?.detail ||
            "You have drafted several itineraries recently. Please try again shortly.",
        });
      } else {
        toast.error("We could not compose that itinerary", {
          description:
            err?.response?.data?.detail ||
            "Please try again in a moment, or send us an enquiry and we will design it by hand.",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const waText = plan
    ? `Hello Azulo Travel, please quote this itinerary:\n\n${plan.trip_title}\n${plan.primary_destination} · ${plan.days.length} days · ${f.travellers} travellers · ${f.month}\nEstimate: ${inr(plan.budget.total_inr)}\n\n${plan.days
        .map((d) => `Day ${d.day}: ${d.title}`)
        .join("\n")}`
    : "";

  return (
    <>
      <Seo
        title="Luxury Trip Planner for Indian Travellers | Azulo Travel"
        description="Draft a tailor-made itinerary from India with hotels, experiences and an INR budget estimate. Refine your AI trip plan with an Azulo Travel designer."
        path="/plan-my-journey"
        breadcrumbs={PLANNER_CRUMBS}
      />
      <PageHero
        image={settings.hero_poster}
        eyebrow="AI Trip Planner"
        title="Plan My Journey"
        subtitle="Tell us how you like to travel. Our AI trip designer drafts a genuine day-by-day itinerary in under a minute — then a human travel designer refines it with you."
        height="short"
      />

      <Section testId="planner-page">
        <form onSubmit={submit} data-testid="planner-form" className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-5 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-6 sm:p-8">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold">Destination or region *</Label>
              <DestinationAutocomplete
                value={f.destination}
                onChange={(v) => set("destination", v)}
                placeholder="Japan, Amalfi Coast, Kenya, Kerala…"
                testId="planner-destination-input"
              />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-semibold">Departing from</Label>
                <Input
                  value={f.origin}
                  onChange={(e) => set("origin", e.target.value)}
                  data-testid="planner-origin-input"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-semibold">Travel month *</Label>
                <Select value={f.month} onValueChange={(v) => set("month", v)}>
                  <SelectTrigger data-testid="planner-month-select">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-baseline justify-between">
                <Label className="text-xs font-semibold">Duration</Label>
                <span data-testid="planner-duration-value" className="text-sm font-semibold text-[var(--brass-2)]">
                  {f.duration_days} days
                </span>
              </div>
              <Slider
                min={3}
                max={21}
                step={1}
                value={[f.duration_days]}
                onValueChange={(v) => set("duration_days", v[0])}
                data-testid="planner-duration-slider"
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-semibold">Travellers</Label>
                <Input
                  type="number"
                  min={1}
                  max={40}
                  value={f.travellers}
                  onChange={(e) => set("travellers", Number(e.target.value))}
                  data-testid="planner-travellers-input"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-semibold">Who is travelling</Label>
                <Select value={f.traveller_type} onValueChange={(v) => set("traveller_type", v)}>
                  <SelectTrigger data-testid="planner-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-semibold">Total budget for all travellers (INR)</Label>
              <Input
                type="number"
                min={25000}
                step={25000}
                value={f.budget_inr}
                onChange={(e) => set("budget_inr", e.target.value)}
                data-testid="planner-budget-input"
              />
              <p className="text-[0.7rem] text-[var(--muted-ink)]">
                Currently {inr(f.budget_inr)} total — about {inr(Math.round(Number(f.budget_inr) / (f.travellers || 1)))} per person
              </p>
            </div>
          </div>

          <div className="flex flex-col rounded-2xl border border-[var(--line)] bg-[var(--surface-warm)] p-6 sm:p-8">
            <Label className="mb-4 text-xs font-semibold">Interests</Label>
            <div data-testid="planner-interests" className="grid grid-cols-2 gap-x-4 gap-y-3">
              {INTERESTS.map((i) => (
                <label
                  key={i}
                  className="flex cursor-pointer items-center gap-2.5 text-[0.82rem] text-[var(--ink-2)]"
                >
                  <Checkbox
                    checked={f.interests.includes(i)}
                    onCheckedChange={() => toggleInterest(i)}
                    data-testid={`planner-interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  />
                  {i}
                </label>
              ))}
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="planner-generate-button"
              className="mt-auto h-13 w-full rounded-xl bg-[var(--brass)] py-4 text-sm font-semibold text-white hover:bg-[var(--brass-2)] active:scale-[0.99]"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Composing your itinerary…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" /> Generate my itinerary
                </>
              )}
            </Button>
            <p className="mt-3 text-center text-[0.7rem] leading-relaxed text-[var(--muted-ink)]">
              Takes 20–40 seconds. Nothing is booked — this is a draft for a human designer to refine.
            </p>
          </div>
        </form>

        {busy && (
          <div
            data-testid="planner-loading"
            className="mt-10 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-10 text-center"
          >
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-[var(--brass)]" />
            <p className="mt-4 font-display text-xl text-[var(--ink)]">
              Designing your {f.duration_days}-day {f.destination} journey
            </p>
            <p className="mt-2 text-sm text-[var(--muted-ink)]">
              Choosing properties, pacing the days and building the budget…
            </p>
          </div>
        )}
      </Section>

      {plan && (
        <Section wash testId="planner-result" id="result">
          {/* animate on mount: whileInView never triggers on small screens because
              this block is far taller than the viewport (results were invisible on mobile) */}
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.div variants={fadeUp} className="mb-10">
              <p className="eyebrow mb-3">Your draft itinerary</p>
              <h2 data-testid="plan-title" className="font-display text-3xl leading-tight text-[var(--ink)] sm:text-4xl">
                {plan.trip_title}
              </h2>
              <p className="mt-4 max-w-3xl text-[0.95rem] leading-relaxed text-[var(--muted-ink)]">
                {plan.summary}
              </p>
              <p className="mt-3 text-sm text-[var(--brass-2)]">{plan.best_time_note}</p>

              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href={planPdfUrl(planId)}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() =>
                    trackItineraryDownload({ planId, destination: plan.primary_destination })
                  }
                  data-testid="plan-download-pdf"
                  className="flex items-center gap-2 rounded-xl bg-[var(--ink)] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--ink-2)]"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </a>
                <a
                  href={waLink(settings.whatsapp, waText)}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-destination={plan.primary_destination}
                  data-testid="plan-whatsapp"
                  className="flex items-center gap-2 rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-6 py-3.5 text-sm font-semibold text-[var(--ink)] transition-colors hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp itinerary
                </a>
                <a
                  href="#quote"
                  data-testid="plan-request-quote"
                  className="flex items-center gap-2 rounded-xl bg-[var(--brass)] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--brass-2)]"
                >
                  Request a quote
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setPlan(null);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  data-testid="plan-restart"
                  className="flex items-center gap-2 rounded-xl px-4 py-3.5 text-sm font-semibold text-[var(--muted-ink)] hover:text-[var(--ink)]"
                >
                  <RefreshCw className="h-4 w-4" /> Start again
                </button>
              </div>
            </motion.div>

            {/* Suggested destinations */}
            <motion.div variants={fadeUp} className="mb-12">
              <p className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                <MapPin className="h-4 w-4 text-[var(--brass)]" /> Suggested destinations
              </p>
              <div data-testid="plan-destinations" className="grid gap-4 sm:grid-cols-3">
                {plan.suggested_destinations.map((d, i) => (
                  <div key={d.name || i} className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-5">
                    <h3 className="font-display text-xl text-[var(--ink)]">{d.name}</h3>
                    <p className="text-xs text-[var(--muted-ink)]">{d.country}</p>
                    <p className="mt-2.5 text-sm leading-relaxed text-[var(--muted-ink)]">{d.why}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Days */}
            <motion.div variants={fadeUp} className="mb-12">
              <p className="mb-4 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                Day by day
              </p>
              <ol data-testid="plan-days" className="space-y-4">
                {plan.days.map((d) => (
                  <li
                    key={d.day}
                    data-testid={`plan-day-${d.day}`}
                    className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-6"
                  >
                    <div className="flex flex-wrap items-baseline gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(176,141,87,0.14)] text-xs font-bold text-[var(--brass-2)]">
                        {d.day}
                      </span>
                      <h3 className="font-display text-xl text-[var(--ink)]">{d.title}</h3>
                      <span className="text-xs text-[var(--muted-ink)]">{d.location}</span>
                    </div>
                    <dl className="mt-4 space-y-2.5">
                      {[["Morning", d.morning], ["Afternoon", d.afternoon], ["Evening", d.evening]].map(
                        ([k, v]) => (
                          <div key={k} className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
                            <dt className="eyebrow shrink-0 sm:w-24">{k}</dt>
                            <dd className="text-[0.9rem] leading-relaxed text-[var(--ink-2)]">{v}</dd>
                          </div>
                        )
                      )}
                    </dl>
                    <p className="mt-3 border-t border-[var(--line)] pt-3 text-xs text-[var(--muted-ink)]">
                      Stay: <span className="font-semibold text-[var(--brass-2)]">{d.stay}</span>
                    </p>
                  </li>
                ))}
              </ol>
            </motion.div>

            <div className="grid gap-8 lg:grid-cols-2">
              <motion.div variants={fadeUp}>
                <p className="mb-4 flex flex-wrap items-center gap-2 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                  <Hotel className="h-4 w-4 text-[var(--brass)]" /> Suggested hotels
                  {plan.hotel_tier && (
                    <span
                      data-testid="plan-tier-badge"
                      className="rounded-full border border-[var(--brass)] bg-[rgba(176,141,87,0.1)] px-3 py-0.5 text-[0.65rem] font-semibold normal-case tracking-normal text-[var(--brass-2)]"
                    >
                      {plan.hotel_tier}
                    </span>
                  )}
                </p>
                <div data-testid="plan-hotels" className="space-y-3">
                  {plan.hotels.map((h, i) => (
                    <div key={h.name || i} className="flex gap-0 overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--surface)]">
                      {h.image && (
                        <img
                          src={h.image}
                          alt={h.name}
                          loading="lazy"
                          data-testid={`plan-hotel-image-${i}`}
                          className="h-auto w-28 shrink-0 object-cover sm:w-32"
                        />
                      )}
                      <div className="min-w-0 p-5">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <h3 className="font-display text-lg text-[var(--ink)]">{h.name}</h3>
                          <span className="text-sm font-semibold text-[var(--brass-2)]">
                            {inr(h.approx_per_night_inr)}/night
                          </span>
                        </div>
                        <p className="eyebrow mt-1">{h.city} · {h.category}</p>
                        <p className="mt-2 text-sm leading-relaxed text-[var(--muted-ink)]">{h.why}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={fadeUp}>
                <p className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                  <Compass className="h-4 w-4 text-[var(--brass)]" /> Suggested experiences
                </p>
                <div data-testid="plan-experiences" className="space-y-3">
                  {plan.experiences.map((x, i) => (
                    <div key={x.name || i} className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-5">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="font-display text-lg text-[var(--ink)]">{x.name}</h3>
                        <span className="text-sm font-semibold text-[var(--brass-2)]">
                          {inr(x.approx_price_inr)}
                        </span>
                      </div>
                      <p className="eyebrow mt-1">{x.city} · {x.duration}</p>
                      <p className="mt-2 text-sm leading-relaxed text-[var(--muted-ink)]">{x.why}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="mt-12">
              <p className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                <Wallet className="h-4 w-4 text-[var(--brass)]" /> Budget estimate
              </p>
              <div data-testid="plan-budget" className="overflow-hidden rounded-xl border border-[var(--line)]">
                {[
                  ["Flights", plan.budget.flights_inr],
                  ["Hotels", plan.budget.hotels_inr],
                  ["Experiences", plan.budget.experiences_inr],
                  ["Transfers", plan.budget.transfers_inr],
                  ["Visa & insurance", plan.budget.visa_and_insurance_inr],
                  ["Azulo concierge fee", plan.budget.azulo_concierge_fee_inr],
                ].map(([label, value], i) => (
                  <div
                    key={label}
                    className={`flex items-center justify-between px-5 py-3.5 text-sm ${
                      i % 2 ? "bg-[var(--surface-warm)]" : "bg-[var(--surface)]"
                    }`}
                  >
                    <span className="text-[var(--muted-ink)]">{label}</span>
                    <span className="font-medium text-[var(--ink)]">{inr(value)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t-2 border-[var(--brass)] bg-[var(--ink)] px-5 py-4">
                  <span className="text-sm font-semibold text-white">Total estimate</span>
                  <span data-testid="plan-budget-total" className="font-display text-2xl text-[#E4CFA9]">
                    {inr(plan.budget.total_inr)}
                  </span>
                </div>
                <div className="flex items-center justify-between bg-[var(--surface)] px-5 py-3.5 text-sm">
                  <span className="text-[var(--muted-ink)]">Per person</span>
                  <span className="font-semibold text-[var(--ink)]">{inr(plan.budget.per_person_inr)}</span>
                </div>
              </div>
            </motion.div>

            <div className="mt-12 grid gap-8 lg:grid-cols-3">
              {[["Inclusions", plan.inclusions], ["Exclusions", plan.exclusions],
                ["Travel notes", plan.travel_tips]].map(([label, items]) => (
                <motion.div key={label} variants={fadeUp}>
                  <p className="mb-3 text-sm font-bold uppercase tracking-wider text-[var(--ink)]">
                    {label}
                  </p>
                  <ul className="space-y-2">
                    {items.map((it, i) => (
                      <li key={typeof it === "string" ? it : it.title || i} className="flex gap-2.5 text-[0.85rem] leading-relaxed text-[var(--muted-ink)]">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[var(--brass)]" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            <motion.div variants={fadeUp} id="quote" className="mt-16 scroll-mt-28">
              <div className="mx-auto max-w-2xl">
                <InquiryForm
                  source="ai_planner"
                  sourceTitle={plan.trip_title}
                  destination={plan.primary_destination}
                  itineraryId={planId}
                  title="Request a quote for this itinerary"
                  subtitle="Your draft itinerary is attached automatically. A travel designer will refine it and price it properly."
                />
              </div>
            </motion.div>
          </motion.div>
        </Section>
      )}

      {!plan && !busy && (
        <Section wash testId="planner-how">
          <SectionHead
            eyebrow="How it works"
            title="Machine draft, human judgement"
            subtitle="The AI is genuinely good at shaping a route and estimating a budget. It is not good at knowing which of four hotels in a town is actually worth staying in. That is what our designers are for."
            align="center"
          />
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              ["1. Tell us the brief", "Budget, destination, month, duration, travellers and interests."],
              ["2. Get a draft in a minute", "A real day-by-day itinerary with hotels, experiences and a costed estimate."],
              ["3. A designer refines it", "We correct the pacing, swap properties we would not book, and price it properly."],
            ].map(([t, b]) => (
              <div key={t} className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-6">
                <h3 className="font-display text-xl text-[var(--ink)]">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--muted-ink)]">{b}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-[var(--muted-ink)]">
            Prefer to speak to a person first?{" "}
            <Link to="/contact" className="font-semibold text-[var(--brass-2)] hover:underline">
              Contact a travel designer
            </Link>
          </p>
        </Section>
      )}
    </>
  );
}
